import type { FaqItem } from './faqData';
import { ANNIVERSARY_FAQ_ITEMS, BIRTHDAY_FAQ_ITEMS } from './faqData';
import type { DemoCard } from '../components/landing/ThemeDemosSection';

export interface LpStepData {
  step: string;
  title: string;
  description: string;
}

export interface LpFaqData {
  q: string;
  a: string;
}

export interface LpEmotiveStatement {
  heading: string;
  highlight: string;
  copy: string;
}

export interface LpPageData {
  slug: string;
  h1: string;
  h1Highlight?: string;
  subheading: string;
  badge?: string;
  ctaLabel?: string;
  metaTitle: string;
  metaDescription: string;
  steps?: LpStepData[];
  faqs: LpFaqData[];
  emotiveStatements?: LpEmotiveStatement[];
  faqItems?: FaqItem[];
  hideWeddingFeatures?: boolean;
  demoCards?: DemoCard[];
  demoSubheading?: string;
}

const DEFAULT_STEPS: LpStepData[] = [
  { step: '1', title: 'Choose your design', description: 'Pick from our curated collection of luxury wedding invitation themes.' },
  { step: '2', title: 'Personalise your invite', description: 'Add your names, date, venue, and style it to match your wedding.' },
  { step: '3', title: 'Send and track', description: 'Share your invite link and watch RSVPs, dietary notes, and guest responses come in.' },
];

export const LP_PAGES: LpPageData[] = [
  {
    slug: 'digital-wedding-invitations',
    h1: 'Digital Wedding Invitations That Do More Than Look Good',
    h1Highlight: 'Digital Wedding Invitations',
    subheading: 'Animated templates your guests will actually remember. Built-in RSVP tracking, guest management, and everything you need to stay on top of your big day. No printing, no postage, no chasing.',
    metaTitle: 'Digital Wedding Invitations | RSVP Tracking | Wedsy',
    metaDescription: 'Stunning digital wedding invitations with real-time RSVP tracking and guest management. All themes included. One price, no per-guest fees.',
    emotiveStatements: [
      {
        heading: 'Your invitation should feel as special as your day',
        highlight: 'as special as your day',
        copy: 'Digital doesn\'t mean generic. Wedsy invitations are animated, deeply personal, and designed to the same standard as premium printed stationery. Without the cost, the lead time, or the postage.',
      },
      {
        heading: 'One price. Every guest. No surprises.',
        highlight: 'No surprises.',
        copy: 'No per-guest fees, no add-ons, no subscription. Pay once and invite everyone, whether that\'s 30 people or 300. Every feature included from the moment you publish.',
      },
      {
        heading: 'Less chasing. More celebrating.',
        highlight: 'More celebrating.',
        copy: 'See exactly who\'s opened their invite, who\'s RSVPed, and who still hasn\'t replied. All from your dashboard. Your digital wedding invitation handles the admin while you focus on everything else.',
      },
    ],
    faqs: [
      { q: 'What is a digital wedding invitation?', a: 'A digital wedding invitation is an animated, personalised invite your guests open via a link, on any device, no app needed. It contains all your wedding details and a built-in RSVP form. No printing, no postage, no waiting.' },
      { q: 'Are digital wedding invitations acceptable?', a: 'Absolutely. Digital wedding invitations are now standard for modern couples. They\'re instant, eco-friendly, and far easier to manage than paper. With Wedsy\'s animated designs, they\'re just as beautiful.' },
      { q: 'Do I need to pay per guest?', a: 'No. Wedsy is a single one-time payment that covers unlimited guests. Invite 30 people or 300. The price is the same.' },
      { q: 'How do I send a digital wedding invitation?', a: 'Once you\'ve designed your invite, share your link via email directly from your Wedsy dashboard, drop it into WhatsApp, or generate a QR code for any printed stationery. Guests click the link and they\'re straight in.' },
    ],
  },
  {
    slug: 'wedding-invitations-online',
    h1: 'The Smarter Way to Send Wedding Invitations Online',
    h1Highlight: 'Wedding Invitations Online',
    subheading: 'Beautiful animated invitations your guests open on any device. RSVP tracking, guest management, and live updates built in. Everything you need, none of the stress.',
    metaTitle: 'Wedding Invitations Online | Send, Track & Manage | Wedsy',
    metaDescription: 'Send beautiful wedding invitations online in minutes. Real-time RSVP tracking, animated designs, and guest management included. One price, no hidden fees.',
    emotiveStatements: [
      {
        heading: 'From design to delivered - the same day',
        highlight: 'the same day',
        copy: 'No print lead times, no postage delays, no proof approvals. Design your wedding invitation online, add your guests, and share a link. Your invitations arrive the moment you send them, wherever your guests are in the world.',
      },
      {
        heading: 'Every response, automatically tracked',
        highlight: 'automatically tracked',
        copy: 'Online wedding invitations mean no stuffed-envelope RSVP cards to chase and no spreadsheets to maintain. Every response lands in your dashboard. Names, plus ones, dietary needs, all of it. Without you lifting a finger.',
      },
      {
        heading: 'Update once. Every guest sees it.',
        highlight: 'Every guest sees it.',
        copy: 'Plans change. Edit your online wedding invitation at any time and every guest sees the latest version instantly. No need to resend a thing.',
      },
    ],
    faqs: [
      { q: 'Can I design my wedding invitation online without any experience?', a: 'Absolutely. Wedsy is built for couples, not designers. Choose a theme, fill in your details through our simple wizard, and your online wedding invitation is ready in minutes.' },
      { q: 'Is it better to send wedding invitations online or by post?', a: 'Online invitations are instant, eco-friendly, and come with built-in RSVP tracking, something paper can\'t match. Many couples send digital invitations to most guests and only print a small number for elderly relatives who prefer paper.' },
      { q: 'How do online wedding invitations work?', a: 'You design your invitation on Wedsy, then share a private link with your guests. They open the link, view all your wedding details, and RSVP in one tap. No app, no account, no friction.' },
      { q: 'Can I update my wedding invitation after sending it?', a: 'Yes. If your venue, time, or any other detail changes, edit your invitation and all guests will see the latest version the next time they visit their link. No need to resend anything.' },
    ],
  },
  {
    slug: 'digital-save-the-date-cards',
    h1: 'Digital Save the Date Cards Your Guests Will Actually Keep',
    h1Highlight: 'Digital Save the Date Cards',
    subheading: 'Announce your date with an animated save the date that lands in seconds, not weeks. Beautifully designed, instantly delivered, and included with your Wedsy invitation at no extra cost.',
    ctaLabel: 'Create Your Save the Date',
    metaTitle: 'Digital Save the Date Cards | Instant Delivery | Wedsy',
    metaDescription: 'Send beautiful digital save the date cards instantly. Animated designs, no postage, no printing. Included with every Wedsy invitation. Unlimited guests.',
    steps: [
      { step: '1', title: 'Choose your save the date theme', description: 'Browse our curated collection and pick the design that fits your wedding style.' },
      { step: '2', title: 'Add your names and date', description: 'Personalise your save the date with your details in minutes. No design experience needed.' },
      { step: '3', title: 'Share with your guests', description: 'Send via email, drop the link into WhatsApp, or generate a QR code. Your save the date arrives instantly.' },
    ],
    emotiveStatements: [
      {
        heading: 'Instant, not eventual',
        highlight: 'Instant',
        copy: 'Your digital save the date cards arrive the moment you send them. No printing lead times, no postage delays, no wondering if they got lost. Share the link and your date is locked in.',
      },
      {
        heading: 'Included. Not an add-on.',
        highlight: 'Included.',
        copy: 'Every Wedsy plan includes both a Save the Date and a full wedding invitation. Same beautiful design, same guest list, same dashboard. No extra cost, no separate tool to figure out.',
      },
      {
        heading: 'Know who\'s seen it before you\'ve even sent the invitation',
        highlight: 'before you\'ve even sent the invitation',
        copy: 'Track exactly who\'s opened their digital save the date and who hasn\'t. Send a gentle nudge to anyone who missed it, all from your dashboard, in one click.',
      },
    ],
    faqs: [
      { q: 'When should I send digital save the date cards?', a: 'Most couples send save the dates 6–12 months before the wedding, or earlier if guests need to travel. Digital save the date cards are ideal because you can send them the moment your date is confirmed. No waiting for printing.' },
      { q: 'What should a digital save the date include?', a: 'Your names, the wedding date, and the general location. You don\'t need all your venue details confirmed yet. That\'s what the full invitation is for. A note that a full invitation will follow is a nice touch.' },
      { q: 'Can I send a save the date before I have all the details?', a: 'Yes, that\'s exactly the point of a save the date. Include your names, date, and location. When you\'re ready, send the full invitation with everything else.' },
      { q: 'Is the save the date included with my Wedsy invitation?', a: 'Yes. Every Wedsy plan includes both a digital save the date and a full wedding invitation. Same design, same guest list, one price.' },
    ],
  },
  {
    slug: 'electronic-wedding-invitations',
    h1: 'Electronic Wedding Invitations That Feel Anything But Digital',
    h1Highlight: 'Electronic Wedding Invitations',
    subheading: 'Forget flat PDFs and generic e-cards. Wedsy electronic wedding invitations are fully animated, deeply personal, and designed to make your guests feel the excitement of your day from the moment they open the link.',
    metaTitle: 'Electronic Wedding Invitations | Animated | Wedsy',
    metaDescription: 'Beautiful electronic wedding invitations with animated designs, RSVP tracking, and guest management. No printing, no postage. Send anywhere..',
    emotiveStatements: [
      {
        heading: 'Not an e-card. A proper invitation.',
        highlight: 'A proper invitation.',
        copy: 'Wedsy electronic invitations are animated, mobile-optimised, and designed to the same standard as premium printed stationery. Without the cost or the lead time. Your guests open a link and experience something genuinely beautiful.',
      },
      {
        heading: 'Send to anyone, anywhere in the world',
        highlight: 'anywhere in the world',
        copy: 'Electronic wedding invitations cross borders instantly. No international postage costs, no customs delays, no wondering if it arrived. Share a link and your guests have everything they need, wherever they are.',
      },
      {
        heading: 'More personal than paper',
        highlight: 'More personal than paper',
        copy: 'Every guest receives their own private, personalised electronic invitation, addressed to them, showing exactly the details relevant to their invite. Nobody sees the wrong thing.',
      },
    ],
    faqs: [
      { q: 'What is an electronic wedding invitation?', a: 'An electronic wedding invitation is an animated, online invite your guests open via a link. It contains all your wedding details and a built-in RSVP form. No paper, no postage, no app required.' },
      { q: 'Are electronic wedding invitations acceptable?', a: 'Absolutely. Electronic wedding invitations are now the norm for many couples. They\'re instant, eco-friendly, and far easier to manage. You can still send paper invitations to guests who prefer them.' },
      { q: 'Do guests need to download an app to view my electronic invitation?', a: 'No. Guests simply click the link you send them and your invitation opens instantly in their browser. Nothing to download, no account required.' },
      { q: 'Can I collect dietary requirements with an electronic invitation?', a: 'Yes. Every Wedsy electronic invitation includes a built-in RSVP form that collects dietary requirements, allergies, plus-one details, and any other questions you want to ask.' },
    ],
  },
  {
    slug: 'wedding-invitations-uk',
    h1: 'Wedding Invitations That Handle Day and Evening Guests Effortlessly',
    h1Highlight: 'Wedding Invitations',
    subheading: 'Day guests. Evening guests. Two sets of details, one seamless invitation. Wedsy handles the split guest list that every other platform ignores. Beautifully.',
    badge: 'Day & Evening Guest Management',
    metaTitle: 'Wedding Invitations UK | Day & Evening Guests | Wedsy',
    metaDescription: 'Digital wedding invitations for UK couples. Day and evening guest management, animated themes, real-time RSVP tracking. One price, unlimited guests..',
    emotiveStatements: [
      {
        heading: 'Day and evening guests, sorted',
        highlight: 'Day and evening guests',
        copy: 'Tag a guest as day or evening and their invitation automatically shows the right schedule, the right venue details, and nothing they don\'t need to know. No separate print runs, no confusing inserts, no awkward wording.',
      },
      {
        heading: 'Beautiful designs for every wedding',
        highlight: 'every wedding',
        copy: 'From countryside barn receptions to city hotel ceremonies, Wedsy themes are designed to feel elegant and personal. Designed to set the right tone for your day from the very first impression.',
      },
      {
        heading: 'One price. No hidden costs.',
        highlight: 'No hidden costs.',
        copy: 'Unlike platforms that charge per guest or per send, Wedsy is a single one-time payment that covers everything. Unlimited guests, both day and evening invitations, RSVP tracking, and full guest management included.',
      },
    ],
    faqs: [
      { q: 'Can I have separate day and evening invitations?', a: 'Yes. Wedsy is built for exactly this. Tag your guests as day or evening and each person automatically sees the right details. Ceremony information for day guests, evening schedule for evening guests. One dashboard, zero confusion.' },
      { q: 'How do I collect dietary requirements from wedding guests?', a: 'Your RSVP form automatically collects dietary requirements and allergies from every guest who responds. You can view and export the full list from your dashboard, perfect for sharing with your caterer.' },
      { q: 'Can Wedsy handle day and evening guests separately?', a: 'Yes, this is one of Wedsy\'s core features. Tag each guest as day or evening and their invitation automatically shows the right schedule and venue details. No separate invitations needed.' },
      { q: 'Do I still need paper invitations?', a: 'Most couples now send digital invitations and only print a small number for elderly relatives or guests who prefer paper. It saves significantly on cost and is far easier to manage.' },
      { q: 'Does Wedsy work for weddings in any country?', a: 'Yes. Wedsy works for any wedding, anywhere. Your invitation can be translated into 15+ languages using the language selector, so guests receive it in their own language no matter where they are in the world.' },
    ],
  },
  {
    slug: 'online-wedding-invitations-rsvp',
    h1: 'Online Wedding Invitations with RSVP and Actually Useful Tracking',
    h1Highlight: 'Online Wedding Invitations with RSVP',
    subheading: "Not just a link with a form attached. Wedsy gives you a real-time dashboard that shows every response, every plus one, every dietary requirement. From the moment your first guest RSVPs.",
    metaTitle: 'Online Wedding Invitations with RSVP Tracking | Wedsy',
    metaDescription: 'Send beautiful online wedding invitations with built-in RSVP tracking. See responses in real time, manage guests, and chase non-replies. No per-guest fees.',
    emotiveStatements: [
      {
        heading: 'Every RSVP, automatically tracked',
        highlight: 'automatically tracked',
        copy: 'Guests respond directly from their online invitation. One tap, no login, no app. Every answer lands in your dashboard the moment they respond. Attending, declining, plus ones, dietary needs, all collected automatically.',
      },
      {
        heading: 'Know exactly where you stand, at any point',
        highlight: 'at any point',
        copy: 'See your confirmed headcount, outstanding responses, and a full breakdown of who\'s coming to what, updated live as guests respond. No counting, no cross-referencing, no spreadsheets.',
      },
      {
        heading: 'Chase non-responders without the awkwardness',
        highlight: 'without the awkwardness',
        copy: 'Filter your guest list to show anyone who hasn\'t RSVPed and send a nudge, all from your dashboard. No individual messages, no group chat announcements. Wedsy handles it.',
      },
    ],
    faqs: [
      { q: 'How does online RSVP for weddings work?', a: 'Your guests open your invitation link and complete the built-in RSVP form in one tap. Their response appears in your dashboard immediately. No manual entry, no email to check.' },
      { q: 'Can I collect dietary requirements when guests RSVP online?', a: 'Yes. Your RSVP form automatically collects dietary requirements and allergies alongside attendance. You can view and export the full list for your caterer at any time.' },
      { q: 'What happens if a guest needs to change their RSVP?', a: 'Guests can update their response any time before your RSVP deadline. You\'ll see the updated answer in your dashboard automatically.' },
      { q: 'Can I set an RSVP deadline?', a: 'Yes. Set your RSVP by date and guests will see it on their invitation. As the deadline approaches, send reminders to anyone who hasn\'t responded. All from your dashboard in one click.' },
    ],
  },
  {
    slug: 'wedding-rsvp-tracking',
    h1: 'Wedding RSVP Tracking That Tells You Everything, Instantly',
    h1Highlight: 'Wedding RSVP Tracking',
    subheading: "No more chasing by text. No more spreadsheet maintenance. Wedsy tracks every RSVP in real time so you always know who's coming, who isn't, and who's been ignoring your invitation for three weeks.",
    metaTitle: 'Wedding RSVP Tracking | Real-Time Dashboard | Wedsy',
    metaDescription: 'Track wedding RSVPs in real time. See who\'s attending, who\'s declined, who hasn\'t replied. Collect plus ones, dietary needs, and more. All included with Wedsy.',
    emotiveStatements: [
      {
        heading: 'Real time, not rough estimates',
        highlight: 'Real time',
        copy: 'The moment a guest responds, your dashboard updates. Confirmed headcount, dietary breakdown, plus-one names. All live, all accurate, without you lifting a finger.',
      },
      {
        heading: 'Everything in one place',
        highlight: 'Everything in one place',
        copy: 'Day guests, evening guests, dietary requirements, plus ones, maybe responses. Every piece of information you need before your wedding is captured through the RSVP and waiting in your dashboard. No cross-referencing multiple sources.',
      },
      {
        heading: 'Chase without the cringe',
        highlight: 'Chase without the cringe',
        copy: 'Filter your outstanding RSVPs and send a reminder in one click. Wedsy sends it for you, polite, personal, and a lot easier than texting your aunts individually at 11pm.',
      },
    ],
    faqs: [
      { q: 'How do I track RSVPs for my wedding?', a: 'With Wedsy, every RSVP appears in your dashboard automatically the moment a guest responds. You can see who\'s accepted, declined, their dietary requirements, and plus-one details, all in one place, updated in real time.' },
      { q: 'Can I export my wedding RSVP list?', a: 'Yes. Export your full guest list and RSVP data as a CSV at any time, ideal for sharing with your venue, caterer, or wedding planner.' },
      { q: 'What if a guest forgets to RSVP?', a: 'Your dashboard shows exactly who hasn\'t responded. Send a reminder directly from Wedsy. No manual chasing, no awkward individual messages.' },
      { q: 'Can I track dietary requirements for each guest?', a: 'Yes. Every RSVP automatically captures dietary needs and allergies per guest. Your caterer gets a clean, accurate list. No gaps, no guesswork.' },
    ],
  },
  {
    slug: 'wedding-guest-list',
    h1: 'Wedding Guest List Management Without the Spreadsheet',
    h1Highlight: 'Wedding Guest List Management',
    subheading: 'Add guests, tag them by group, track their responses, collect dietary requirements, and manage plus ones, all from one dashboard that updates in real time.',
    metaTitle: 'Wedding Guest List Management | Organise Guests | Wedsy',
    metaDescription: 'Manage your entire wedding guest list in one place. Tag day and evening guests, track RSVPs, collect dietary needs, and export anytime.',
    emotiveStatements: [
      {
        heading: 'One place for everything',
        highlight: 'One place for everything',
        copy: 'Names, email addresses, tags, RSVP status, dietary requirements, plus-one details. Every piece of information about every guest lives in your Wedsy dashboard. No switching between a spreadsheet, a notes app, and a text thread.',
      },
      {
        heading: 'Day guests and evening guests, handled',
        highlight: 'Day guests and evening guests',
        copy: 'Tag guests as day or evening and Wedsy does the rest. Each person sees only the invitation details relevant to them, and your dashboard shows your lists separately so your headcounts are always accurate.',
      },
      {
        heading: 'Import what you have. Export what you need.',
        highlight: 'Export what you need.',
        copy: 'Already have a guest list in a spreadsheet? Import it straight into Wedsy. Need to share final numbers with your venue or caterer? Export the whole thing as a CSV in one click.',
      },
    ],
    faqs: [
      { q: 'How do I manage my wedding guest list online?', a: 'Wedsy gives you a live guest dashboard showing every guest\'s RSVP status, dietary requirements, and plus-one details, updated automatically as guests respond. Add guests individually or import from a spreadsheet.' },
      { q: 'Can I export my wedding guest list?', a: 'Yes. Download your complete guest list as a spreadsheet at any time, ideal for sharing with your venue, caterer, or wedding planner.' },
      { q: 'How do I track dietary requirements for wedding guests?', a: 'Every guest who RSVPs through Wedsy is automatically asked for dietary requirements and allergies. You see the complete list in your dashboard and can export it for your caterer.' },
      { q: 'Can I manage day and evening guests separately?', a: 'Yes. Tag guests as day or evening and Wedsy manages their invitation details separately. Your dashboard shows each group\'s RSVPs independently so your headcounts are always accurate.' },
    ],
  },
  {
    slug: 'send-wedding-invitations-online',
    h1: 'Send Your Wedding Invitations Online Today: Not in Six Weeks',
    h1Highlight: 'Send Wedding Invitations Online',
    subheading: 'Design a beautiful animated invitation, add your guests, and send. Your invitations arrive instantly, by email, WhatsApp, or shareable link. No printing delays, no postage costs, no waiting.',
    ctaLabel: 'Start Sending',
    metaTitle: 'Send Wedding Invitations Online | Instantly | Wedsy',
    metaDescription: 'Send stunning wedding invitations online in minutes. Animated designs, instant delivery via email or WhatsApp, real-time RSVP tracking. No printing..',
    emotiveStatements: [
      {
        heading: 'From design to delivered - the same day',
        highlight: 'the same day',
        copy: 'Choose your theme, add your details, import your guest list, and send. No print lead time, no proof approval, no postage to organise. When you\'re ready to send, you send.',
      },
      {
        heading: 'Send it however your guests will actually open it',
        highlight: 'actually open it',
        copy: 'Email directly from your dashboard. Drop the link in WhatsApp. Generate a QR code for any physical stationery. However your guests prefer to receive their invitation. Wedsy makes it effortless.',
      },
      {
        heading: 'Guests anywhere in the world. No extra cost.',
        highlight: 'No extra cost.',
        copy: 'No international postage, no overseas delivery delays, no wondering if it arrived. Your invitation reaches guests in Sydney, New York, or Edinburgh exactly the same way - instantly.',
      },
    ],
    faqs: [
      { q: 'How do I send my wedding invitations online?', a: 'Design your invitation on Wedsy, then send via email directly from your dashboard, drop your invite link into WhatsApp, or share it any other way you like. Guests click the link and they\'re straight in.' },
      { q: 'Can I send to guests one by one or all at once?', a: 'Both. Share one link for all guests, or create personalised individual links with your own tracked invites per guest. It\'s entirely up to you.' },
      { q: 'What do guests need to do to receive my invitation?', a: 'Nothing, they just click the link you send them. No app to download, no account to create. Your invitation opens instantly in their browser.' },
      { q: 'How do I know my guests have received their invitation?', a: 'Your Wedsy dashboard shows when guests open your invitation and when they RSVP. You can see at a glance who\'s engaged and who might need a reminder.' },
    ],
  },
  {
    slug: 'day-and-evening-wedding-invitations',
    h1: 'One Invitation. Day Guests and Evening Guests, Handled.',
    h1Highlight: 'Day and Evening Wedding Invitations',
    subheading: 'Wedsy is built for the way weddings actually work, with two guest lists, two sets of details, and one platform that manages all of it without the confusion.',
    badge: 'Built for Split Guest Lists',
    metaTitle: 'Day and Evening Wedding Invitations | Wedsy',
    metaDescription: 'Manage day and evening wedding guests from one invitation. Each guest sees only their details. No separate invites, no confusing inserts. Included with Wedsy.',
    emotiveStatements: [
      {
        heading: 'Each guest sees only what\'s relevant to them',
        highlight: 'only what\'s relevant to them',
        copy: 'Tag a guest as day or evening and their invitation automatically shows the right schedule, the right venue details, and nothing they don\'t need. No confusion, no accidental ceremony reveals, no awkward phone calls.',
      },
      {
        heading: 'No separate invitations to manage',
        highlight: 'No separate invitations',
        copy: 'Day and evening wedding invitations in one place. Same dashboard, same theme, same guest list. You manage everything together, and Wedsy handles the personalisation for each guest automatically.',
      },
      {
        heading: 'Accurate headcounts for both lists, always',
        highlight: 'Accurate headcounts',
        copy: 'Your dashboard shows day and evening RSVPs separately. Ceremony headcount, reception headcount, evening headcount. All live, all accurate, without any manual counting.',
      },
    ],
    faqs: [
      { q: 'Can I have different invitations for day and evening guests?', a: 'Yes. Wedsy lets you manage day and evening guests on one account. Tag each guest as day or evening and they automatically receive a version of the invitation with only the details relevant to them.' },
      { q: 'How do I manage day and evening RSVPs separately?', a: 'Your dashboard shows day and evening guests clearly separated. You can see RSVPs, dietary requirements, and plus ones for each group independently, exactly the breakdown your venue and caterer need.' },
      { q: 'Do day and evening guests get different invitation links?', a: 'You can share one invitation link with all guests and Wedsy personalises the experience based on their tag. Or manage separate private links per guest for full control. Either approach works.' },
      { q: 'Can I move a guest from evening to day after sending?', a: 'Yes. Update a guest\'s tag at any time and their invitation updates immediately. They\'ll see the correct details the next time they open their link. No need to resend.' },
    ],
  },
  {
    slug: 'anniversary-invitations',
    h1: 'Beautiful Digital Anniversary Invitations',
    h1Highlight: 'Anniversary Invitations',
    subheading: 'Celebrate your milestone beautifully. Stunning digital invitations with online RSVP and guest management, all for just £19.',
    badge: 'Anniversary',
    ctaLabel: 'Create Your Invite',
    metaTitle: 'Digital Anniversary Invitations | Wedsy',
    metaDescription: 'Create beautiful digital anniversary invitations in minutes. Stunning designs, online RSVP, and guest management. All for just £19.',
    faqs: [],
    faqItems: ANNIVERSARY_FAQ_ITEMS,
    hideWeddingFeatures: true,
    demoSubheading: 'Real anniversary invites — tap to open one as your guests will.',
    demoCards: [
      {
        id: 'typographic',
        screenshotId: 'anniversary-typographic',
        label: 'Typographic',
        desc: 'Editorial type-led design with refined elegance',
        url: 'https://wedsy.co/i/demo-anniversary-typographic',
        names: 'Sarah & Thomas',
        occasion: '25th Anniversary · The Old Mill, Cirencester',
      },
      {
        id: 'sage',
        screenshotId: 'anniversary-sage',
        label: 'Sage',
        desc: 'Editorial type-led design in soft sage and green tones',
        url: 'https://wedsy.co/i/demo-anniversary-sage',
        names: 'Helen & David',
        occasion: '10th Anniversary · Rosewood House, Bath',
      },
    ],
    emotiveStatements: [
      {
        heading: 'Celebrate the years, beautifully',
        highlight: 'beautifully',
        copy: 'Your anniversary deserves more than a group chat message. Wedsy gives you a stunning animated invitation that sets the tone from the moment guests open it. Elegant, personal, and effortless.',
      },
      {
        heading: 'Online RSVP built in. No chasing required.',
        highlight: 'No chasing required.',
        copy: 'Guests reply directly from their invitation. You see every response in real time: who\'s coming, dietary needs, plus ones. All in one place, no spreadsheet needed.',
      },
      {
        heading: 'One price. Everything included.',
        highlight: 'Everything included.',
        copy: 'Just £19, a single one-time payment that covers unlimited guests, your choice of design and palette, RSVP tracking, and sharing by link, email, WhatsApp, or QR code. No hidden fees, no renewal.',
      },
    ],
  },
  {
    slug: 'birthday-invitations',
    h1: 'Digital Birthday Invitations for Any Milestone',
    h1Highlight: 'Birthday Invitations',
    subheading: 'Make your milestone birthday unforgettable. Stunning digital invitations with online RSVP and guest management, all for just £19.',
    badge: 'Birthday',
    ctaLabel: 'Create Your Invite',
    metaTitle: 'Digital Birthday Invitations | Milestone Celebrations | Wedsy',
    metaDescription: 'Create beautiful digital birthday invitations in minutes. Stunning designs, online RSVP, and guest management. Perfect for milestone birthdays. All for just £19.',
    faqs: [],
    faqItems: BIRTHDAY_FAQ_ITEMS,
    hideWeddingFeatures: true,
    demoSubheading: 'Real celebration invites — tap to open one as your guests will.',
    demoCards: [
      {
        id: 'typographic',
        screenshotId: 'anniversary-typographic',
        label: 'Typographic',
        desc: 'Editorial type-led design with refined elegance',
        url: 'https://wedsy.co/i/demo-anniversary-typographic',
        names: 'Charlotte turns 40',
        occasion: '40th Birthday · The Grand Hotel, Edinburgh',
      },
      {
        id: 'sage',
        screenshotId: 'anniversary-sage',
        label: 'Sage',
        desc: 'Soft sage tones with a warm, organic feel',
        url: 'https://wedsy.co/i/demo-anniversary-sage',
        names: 'James turns 50',
        occasion: '50th Birthday · Rosewood House, Bath',
      },
    ],
    emotiveStatements: [
      {
        heading: 'A birthday worth celebrating properly',
        highlight: 'worth celebrating properly',
        copy: "Milestone birthdays deserve more than a Facebook event. Wedsy gives you a stunning animated invitation that sets the right tone before guests even arrive. Elegant, personal, and effortless to put together.",
      },
      {
        heading: 'RSVPs sorted. No chasing.',
        highlight: 'No chasing.',
        copy: "Guests reply directly from the invite. You see every response in real time, who's coming, dietary needs, plus ones. All in one place, no spreadsheet required.",
      },
      {
        heading: 'One price. Everything included.',
        highlight: 'Everything included.',
        copy: 'Just £19, a single one-time payment that covers unlimited guests, your choice of design and palette, RSVP tracking, and sharing by link, email, WhatsApp, or QR code. No hidden fees, no renewal.',
      },
    ],
  },
];

export const LP_SLUGS = LP_PAGES.map(p => p.slug);
export const LP_PAGE_MAP = Object.fromEntries(LP_PAGES.map(p => [p.slug, p]));

export { DEFAULT_STEPS };